import { identity } from 'lodash';
import { Draft } from '@reduxjs/toolkit';

import createSortByQueryParam, { RawSortBy } from '../../createSortByQueryParam';
import { FIELDS } from '../constants';

import {
    BaseQueryParams,
    EnhancedState,
    PaginationConfigWithParentKey,
    PaginationMixin,
    PaginationRoutineName,
} from './createEnhancedRoutineSlice.types';

/**
 * A function to enhance routineReducers with pagination routines
 */
const getPaginationMixinRaw = <ParentKey extends string | undefined = undefined>({
    parentKey,
    defaultSortParam,
    sortParamToUpperCase,
}: PaginationConfigWithParentKey<ParentKey>): PaginationMixin<ParentKey> => {
    const getField: (state: any) => EnhancedState = parentKey
        ? (x: Record<NonNullable<ParentKey>, Draft<EnhancedState>>) => x[parentKey]
        : identity<Draft<EnhancedState>>;

    const getRoutineName = parentKey
        ? <N extends PaginationRoutineName>(name: N) => `${name}_${parentKey}`
        : identity<PaginationRoutineName>;

    const setQueryParam = (
        state: Draft<EnhancedState>,
        field: keyof BaseQueryParams,
        value: any,
    ) => {
        (getField(state)[FIELDS.QUERY_PARAMS] as Record<keyof BaseQueryParams, any>)[field] = value;
    };

    return {
        [getRoutineName('setSearch')]: {
            TRIGGER(state: Draft<EnhancedState>, query: BaseQueryParams['query']) {
                setQueryParam(state, FIELDS.QUERY, query || undefined);
                setQueryParam(state, FIELDS.PAGE, 0);
            },
        },

        [getRoutineName('setPage')]: {
            TRIGGER(state: Draft<EnhancedState>, page: BaseQueryParams['page']) {
                getField(state)[FIELDS.LOADING] = true;
                setQueryParam(state, FIELDS.PAGE, page);
            },
        },

        [getRoutineName('setPageSize')]: {
            TRIGGER(state: Draft<EnhancedState>, pageSize: BaseQueryParams['size']) {
                setQueryParam(state, FIELDS.SIZE, pageSize);
                setQueryParam(state, FIELDS.PAGE, 0);
            },
        },

        [getRoutineName('setSortBy')]: {
            TRIGGER(state: Draft<EnhancedState>, sortBy: RawSortBy) {
                setQueryParam(
                    state,
                    FIELDS.SORT,
                    createSortByQueryParam(sortBy, defaultSortParam, sortParamToUpperCase),
                );
                setQueryParam(state, FIELDS.PAGE, 0);
            },
        },
    } as PaginationMixin<ParentKey>;
};

export const defaultPaginationMixin = Object.freeze(getPaginationMixinRaw({}));

const getPaginationMixin = (config: PaginationConfigWithParentKey<any>) => {
    if (config.parentKey) return getPaginationMixinRaw(config);

    if (config.defaultSortParam || config.sortParamToUpperCase !== undefined) {
        const { setSortBy } = getPaginationMixinRaw(config);
        return { ...defaultPaginationMixin, setSortBy };
    }

    return defaultPaginationMixin;
};

export default getPaginationMixin as typeof getPaginationMixinRaw;
